/* A page's BLOCKS — the part the Website panel and the Funnels panel share.
 *
 * A funnel page-step holds exactly what a website page holds: an ordered list of
 * typed copy blocks, written either by hand or beside Brill. `funnel_sections`
 * is column-for-column `website_sections` (see utils/sectionStore.js), so the
 * editor is the same editor. This file is it.
 *
 * NOTHING IN HERE KNOWS WHAT A WEBSITE PAGE IS, or a funnel step. It is handed a
 * `page` — anything with a title and an address — and a section. What the page
 * IS, and anything Brill should be told about the path it sits on, comes in as
 * props from the panel that owns it. That boundary is what stopped website.jsx
 * becoming the shared file by default.
 */
import { MODULE_LABELS, buildMomentumContext } from "../../utils/momentumContext";
import { useDrafterKeep, usePageChat } from "../../utils/pageChat";
import { offerLine } from "../../utils/offerLine";
import { PAGE_SECTIONS_PER_REPLY, applySectionBlock, buildPageContext, buildSectionContext, parsePageBlock, parseSectionBlock, stripPageBlock, stripSectionBlock } from "../../utils/sectionBlock";
import { HERO_SECTION } from "../../utils/websitePages";
import { SECTION_KINDS, hasContent, kindDef, kindLabel, pictureOf, sectionSpecLines, sectionWords, withPicture } from "../../utils/websiteSectionKinds";
import { limitsFor, styleLine } from "../../utils/siteStyles";
import { SECTION_DRAFT, hiddenFieldKeys, isFromMyWebsite, revertSection } from "../../utils/websiteSections";
import { Block } from "./block";
import { PictureField } from "./fields";
import { BlockPager, Drafter } from "./drafter";
import { B, btnGhost, input, nb } from "./tokens";
import { Menu2, SourceTag } from "./ui";

/* ONE call to Brill for one section, shared by the SPARKLE and the drafting
   chat so the context is assembled once. Throws with a sentence the coach can
   read; both callers show it where she is looking. The mirror of
   `courseDraftReply` in course.jsx, and deliberately the same shape — a block
   is a block.

   COST: one API call per invocation. Her locked work rides along on every
   message, scoped by CONTEXT_SCOPES.website_copy to what website copy needs,
   plus whatever `extraScope` this record asks for. A chat is therefore dearer
   per result than a sparkle, because the conversation grows and re-sends with
   each turn — the sparkle is exactly one round trip.                          */
export async function sectionDraftReply({
  token, page, kind, section, content, chatContext = {}, contextLines: contextLinesIn = [], extraScope = null, messages,
  /* THE ONE OFFER THIS PAGE SERVES, not the ladder. Until 2026-09-18 nothing
     offer-shaped reached Brill here at all: `website_pages.offer_id` has
     existed since 0015 and the drafting call never read it, so Brill wrote her
     banner without knowing what it was selling. Her words, from the screen:
     "it is unclear that it knows your offer." It was not unclear — it did not.
     Scripts and Presentations already sent theirs; Sites was the odd one.
     COST: one line per message, and only when a page HAS a linked offer. */
  offer = null,
  /* THE WHOLE PAGE: every section, with her words as they stand on screen.
     COST: every section's fields and words ride on each message, so a page
     turn is dearer than a section turn — and a reply can run to four
     sections, hence the larger ceiling (you pay for what is written, not the
     headroom). */
  pageSections = null,
}) {
  /* SAYING WHICH OFFER IS NOT ENOUGH ON ITS OWN. Her locked My Offer — the
     flagship — rides in the context above this on every website call, in far
     more words than one line of offer. On 2026-09-24 a landing page for her
     Archetype Assessment, a free lead magnet, came back selling the eight-week
     flagship. So the line says which of the two this page is for — and, her
     follow-up the same day, that the flagship still THREADS through: "offer
     takes priority but still look at the offer in the accelerator". A lead
     magnet's page should read as a door into her world, not a stranger's. */
  /* A STYLED PAGE (utils/siteStyles) tells Brill the counts are the room the
     layout has. Nothing at all for a page with no style: its call is exactly
     what it was. COST: one line per message, only on styled pages. */
  const styled = styleLine(page?.style);
  const contextLines = styled ? [styled, ...contextLinesIn] : contextLinesIn;
  const lines = offer
    ? [`Offer this page serves: ${offerLine(offer)}`,
       "Write this page for THAT offer — it is what this page sells. Keep it threaded to her Signature Offer from the Accelerator (her client, her language, where this leads in her bigger work), but do not sell the Signature Offer here unless it is the same offer.",
       ...contextLines]
    : contextLines;
  const ctx = buildMomentumContext({
    moduleId: "website_copy",
    extraScope,
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (id, name) => {
      const stamp = chatContext.outputStamps?.[id]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "website_copy",
      max_tokens: pageSections ? 4000 : 2000,   // model is chosen server-side; covers Sonnet 5's ~30% larger token counts
      moduleLabel: "Website copy",
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        /* The field list is built from the kind spec and sent per message,
           which is what keeps api/_lib/prompts.js from holding a second copy of it.
           Rebuilt from `content` each time so Brill edits what's on screen now,
           not what was there when the chat opened. */
        { role: "user", content: pageSections
          ? buildPageContext({ page, sections: pageSections.map((x) => ({ ...x, limits: limitsFor(x, page?.style) })), extraLines: lines })
          : buildSectionContext({ page, kind, section: { ...section, content }, specLines: sectionSpecLines(kind.id, limitsFor(section, page?.style)), extraLines: lines }) },
        ...messages,
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  // 429 and 403 already carry a written explanation; anything else gets one.
  if (!res.ok) throw new Error(data?.error || "Something went wrong reaching Brill — try again in a moment. Nothing in this section was touched; you can always type it in yourself.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}


/* The Add section menu. The eight named kinds tell her what a page is made of
   without her having to know the words for it; Other is the escape hatch, and is
   exactly what every section was before the kinds existed. Same shape as the Add
   page menu, so it's one pattern learned once. */
export const KIND_ICONS = {
  banner: "🎯", about: "👋", services: "💼", freebie: "🎁",
  testimonials: "💬", faq: "❓", cta: "👉", contact: "✉️", other: "✏️",
};


export function AddSectionMenu({ open, setOpen, onAdd }) {
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(!open)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>+ Add section</button>
      {open && (
        <Menu2 head="What kind of section?" items={SECTION_KINDS.map((k) => [
          KIND_ICONS[k.id] || "✏️", k.label, k.hint, false,
          () => { setOpen(false); onAdd(k.id); },
        ])} />
      )}
    </div>
  );
}


/* GenerateBadge moved to block.jsx with the rest of the row (item 5,
   2026-09-16). Re-exported here so nothing that imported it from this file
   has to change. */
export { GenerateBadge } from "./block";


/* One section, collapsed by default.
 *
 * Collapsed it is a header strip only: grip, chevron, the KIND, and how much is
 * written. The heading used to sit here instead of the kind, and the row also
 * carried a kind picker, a status toggle and two arrows — which is what made a
 * page of eight sections read as a wall of controls rather than a page.
 *
 * What moved rather than went:
 *   - the kind picker is in edit mode. Retyping a section is still how a block
 *     split out of her locked prose becomes a real Banner, and it is still
 *     non-destructive (see `changeKind` in utils/websiteSections.js).
 *   - the Draft/Done toggle is GONE, 2026-09-15. Done is marked on the PAGE
 *     now, beside its name — her call, and the better one: nine sections meant
 *     nine small decisions to finish one page, and none of them was the
 *     question she was actually asking herself. A section is written or not.
 *   - the arrows are gone, replaced by the grip.
 *
 * Reading and editing are separate: open shows the copy, the pencil switches to
 * fields. Every row keeps the same four actions whether or not anything is
 * written in it yet — an empty section is exactly where she reaches for the
 * pencil.
 */
export function SectionBlock({ section, onSave, onDelete, onChangeKind, onDraft, onSparkle, sparkling, startOpen, dragHandle, dragging, pageStyle = null,
  /* {type, id, label} — what a Library pick would be used BY (this page). The
     picker is only offered where the caller says this, because the Library has
     to be able to record where the quote went. */
  usedAs = null }) {
  const kind = kindDef(section?.sectionType);
  const fromSource = isFromMyWebsite(section);
  /* The row itself is the shared Block (block.jsx) — this wrapper only says
     what a WEBSITE section adds: its kind picker, "From My Website", word
     budgets, and copy the current kind hides. */

  return (
    <Block
      kind={kind} noun="section"
      content={section?.content || {}}
      words={(content) => sectionWords({ ...section, content })}
      /* WRITTEN IS DRAFT. DONE IS HER MARK (6.1, restated 2026-09-15). The first
         save used to write SECTION_DONE, so a block Brill drafted and she never
         read marked itself finished. Whatever the section already is SURVIVES
         an edit; only the fallback is Draft. */
      onCommit={(content) => onSave({ ...section, content, status: section?.status || SECTION_DRAFT })}
      /* Cancel puts her words back AND the status, not the kind — see
         revertSection in utils/websiteSections.js (spec 5.1). Retyping goes
         through `onChangeKind`, which is non-destructive by design, and
         reverting it here would fight that handler for the same row. */
      snapshot={() => ({ content: section?.content || {}, status: section?.status || null })}
      onCancel={(before) => {
        const undo = revertSection(section, before);
        if (!undo) return null;
        onSave(undo);
        return undo.content || {};
      }}
      sparkle={{ onClick: () => onSparkle?.(section), busy: sparkling }}
      onChat={() => onDraft(section)}
      onClear={() => onDelete(section)} clearLabel="Delete section"
      tag={fromSource ? <SourceTag label="From My Website" /> : null}
      editTop={(content, change, commit) => (<>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {/* Retyping a section is how a block split out of her locked prose
              becomes a real Banner. Non-destructive (`changeKind`). Done is NOT
              asked here — it is marked on the page (her call, 2026-09-15). */}
          <select value={kind.id} onChange={(e) => onChangeKind(section, e.target.value)} aria-label="Section kind"
            style={{ ...input, width: "auto", padding: "6px 9px", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {SECTION_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </div>
        {/* THE PICTURE, at the top (her ask, 2026-10-23: "probably at the
            top"), for the kinds that carry one. Paste a link or Choose image —
            her GHL library, Canva inside it. Kept in content.pictures, never a
            field, so Brill neither counts it nor writes it (`pictureOf`). */}
        {kind.picture && (
          <PictureField guide={kind.picture.toLowerCase()} value={pictureOf(content)}
            onChange={(url, now) => change(withPicture(content, url), now)} onBlur={commit}
            emptyHint="No picture yet? Leave it empty — the preview and the GHL prompt keep a labelled spot for it." />
        )}
      </>)}
      readBottom={kind.picture && pictureOf(section?.content) ? (
        <img src={pictureOf(section.content)} alt="" style={{ display: "block", maxHeight: 120, maxWidth: "100%", borderRadius: 8, border: `1px solid ${B.border}`, marginTop: 12 }} />
      ) : null}
      editBottom={(content) => hiddenFieldKeys({ ...section, content }).length > 0 && (
        /* Copy that exists but isn't on screen is indistinguishable from copy
           that was deleted, so say where it went. */
        <div style={{ fontSize: 12, color: B.dim, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 9, padding: "9px 12px", marginBottom: 14 }}>
          This section still holds copy that <b style={nb}>{kind.label}</b> doesn't show. Switch it back to see it — nothing was deleted.
        </div>
      )}
      fieldProps={{
        limits: limitsFor(section, pageStyle),
        onLimits: (key, words) => onSave({ ...section, limits: { ...(section?.limits || {}), [key]: words } }),
        usedAs,
      }}
      startOpen={startOpen} dragHandle={dragHandle} dragging={dragging}
    />
  );
}


/* Section to section without going back out to the list. The pager itself is
   the shared BlockPager (drafter.jsx); this says what a section's dot reads —
   written or not, since Done moved to the page. */
export function SectionPager({ sections = [], index, onGo }) {
  return (
    <BlockPager index={index} onGo={onGo} noun="Section"
      items={sections.map((s) => ({ id: s.id, label: kindLabel(s.sectionType), written: hasContent(s) }))} />
  );
}


/* Writing one section with Brill — the shared Drafter (drafter.jsx), which IS
   this screen, lifted out on 2026-09-16 so Courses and Outreach draw the same
   one. What stays here is what only a website section knows: how to ask Brill
   (`sectionDraftReply`), how to read her answer, the chips for website copy,
   and word budgets. */
export function SectionDrafter({
  page, section, chatContext = {}, sections = [], onGoToSection, onSave, onBack,
  /* What the panel knows and this file doesn't: extra lines for Brill about the
     page's job, the opening line, the way back, and the control that moves to
     another page. */
  contextLines = [], opener, backLabel = "← Back to the page", picker = null, usedAs = null, offer = null, where = null,
  /* True when the PANEL's bar is already carrying the way back and the record
     picker. Then this screen draws neither. */
  chromeInBar = false,
  /* Extra locked sections THIS record needs, on top of what website copy always
     gets — see `contextScopeFor` in websitePages.js. */
  extraScope = null,
  /* The next page, for Next once this page has nothing left to write. */
  nextRecord = null,
  /* "block" or "record" — which scope opens. Record is the whole page. */
  startScope = "block",
  /* What the WHOLE page carries on top of website copy — every section's
     extras together (an About block anywhere brings her story). */
  pageScope = null,
}) {
  const kind = kindDef(section?.sectionType);

  /* THE CHAT IS KEPT, per page (supabase/0048): this section's under its id,
     the whole page's under "page". Read once as the drafter opens. */
  const keep = useDrafterKeep(usePageChat(page?.id, sections.map((x) => x.id)), section?.id);

  // Every chip reflects real content, so the row can be trusted. See ContextChips.
  const has = (mod, name) => {
    const outputs = chatContext.acceleratorOutputs?.[mod] || {};
    return name ? !!outputs[name] : Object.keys(outputs).length > 0;
  };
  const written = Object.values(section?.content || {}).some((v) => (Array.isArray(v) ? v.length : String(v || "").trim()));
  const contextItems = [
    { label: "Your voice", on: has("voice", "Voice Profile") },
    { label: "Positioning", on: has("positioning") },
    /* THREE THINGS IN ONE CHIP, her design: teal and NAMED when this page has
       a linked offer, so the thing buried in the page's settings cog is
       uncovered exactly where it matters; teal when her Accelerator offer is
       locked, which Brill reads anyway; and otherwise it says what to do about
       it rather than only that it is missing. Presentations already read both
       sources — this makes Sites agree with it. */
    offer
      ? { label: `Offer: ${offer.name || "Untitled offer"}`, on: true }
      : { label: has("offer", "Your Signature Offer") ? "Your offer" : "Link an offer", on: has("offer", "Your Signature Offer") },
    { label: "Hero statement", on: has("website", HERO_SECTION) },
    /* The section's own copy is context too — what makes a rewrite an edit. */
    { label: `${kind.label} so far`, on: written },
    /* Whatever this record asked for on top, built from the same `extraScope`
       that is actually sent, so the row cannot claim one thing while the call
       carries another. */
    ...Object.entries(extraScope || {}).map(([mod, names]) => ({
      label: MODULE_LABELS[mod] || mod,
      on: (names || []).some((n) => has(mod, n)),
    })),
  ];

  const index = sections.findIndex((x) => x.id === section?.id);

  /* THE WHOLE PAGE'S CHIPS. It sends every section's words and every
     section's extras (an About block anywhere brings her story), so its row
     says so — built from `pageScope`, the same object the call carries. */
  const pageItems = [
    ...contextItems.slice(0, 4),
    { label: "The page so far", on: sections.some((x) => hasContent(x)) },
    ...Object.entries(pageScope || extraScope || {}).map(([mod, names]) => ({
      label: MODULE_LABELS[mod] || mod,
      on: (names || []).some((n) => has(mod, n)),
    })),
  ];

  return (
    <Drafter
      /* Recording the voice (supabase/0030): where a Lock lands. */
      voice={{ tool: "website_copy", recordType: "website_section", blockId: section?.id }}
      keep={keep}
      libraryKinds={["story"]}
      kind={kind}
      content={section?.content || {}}
      /* `onSave` is the section store's save: the saved row, or null with the
         error already on the store. */
      onCommit={(content) => onSave({ ...section, content })}
      ask={({ token, messages, content }) => sectionDraftReply({
        token, page, kind, section, content, chatContext, contextLines, extraScope, offer, messages,
      })}
      parse={(reply, content) => {
        const block = parseSectionBlock(reply, kind.id);
        return block ? applySectionBlock(content, block, kind.id) : null;
      }}
      strip={stripSectionBlock}
      opener={opener || `I'm on the ${kind.label.toLowerCase()} section of your ${page?.title || "page"}. I can draft it from what you've locked, or you tell me the angle and I'll write to that. Which way?`}
      contextItems={contextItems}
      pager={{
        items: sections.map((x) => ({ id: x.id, label: kindLabel(x.sectionType), written: hasContent(x) })),
        index: index < 0 ? 0 : index,
        onGo: (id) => onGoToSection?.(id),
        noun: "Section",
      }}
      backLabel={backLabel} onBack={onBack} picker={picker} chromeInBar={chromeInBar} nextRecord={nextRecord}
      where={where}
      fieldProps={{
        limits: limitsFor(section, page?.style),
        onLimits: (key, words) => onSave({ ...section, limits: { ...(section?.limits || {}), [key]: words } }),
        usedAs,
      }}
      startScope={startScope}
      /* THE WHOLE PAGE (2026-09-16) — every section's fields on the right, one
         reply can fill several, one Lock writes them. Sections are their own
         rows, so each is saved on its own. */
      record={{
        blockLabel: "Section",
        label: "Page",
        noun: "page",
        title: `Write ${page?.title || "this page"}`,
        heading: page?.title || "Untitled page",
        contextItems: pageItems,
        opener: `We're on the whole of your ${page?.title || "page"} — ${sections.length} section${sections.length === 1 ? "" : "s"}. Say "draft the page" and I'll write it top to bottom${sections.length > PAGE_SECTIONS_PER_REPLY ? `, ${PAGE_SECTIONS_PER_REPLY} sections at a time` : ""}, or tell me what this page has to do first.`,
        suggestions: {
          first: ["Draft the page", "I'll tell you what it's for"],
          later: sections.length > PAGE_SECTIONS_PER_REPLY
            ? ["Keep going", "Make them fit together", "Make it sound more like me"]
            : ["Make them fit together", "Shorter", "Make it sound more like me"],
        },
        blocks: sections.map((x) => ({
          id: x.id, kind: kindDef(x.sectionType), content: x.content || {},
          fieldProps: {
            limits: limitsFor(x, page?.style),
            onLimits: (key, words) => onSave({ ...x, limits: { ...(x.limits || {}), [key]: words } }),
            usedAs,
          },
        })),
        ask: ({ token, messages, contents }) => sectionDraftReply({
          token, page, kind, section, content: contents[section?.id] || section?.content || {},
          chatContext, contextLines, extraScope: pageScope || extraScope, offer, messages,
          pageSections: sections.map((x) => ({ ...x, content: contents[x.id] || x.content || {} })),
        }),
        parse: (reply, contents) => {
          const bySection = parsePageBlock(reply, sections);
          if (!bySection) return null;
          const out = {};
          for (const x of sections) {
            if (bySection[x.id]) out[x.id] = applySectionBlock(contents[x.id] || {}, bySection[x.id], x.sectionType);
          }
          return Object.keys(out).length ? out : null;
        },
        strip: stripPageBlock,
        onCommitBlock: (id, content) => {
          const x = sections.find((y) => y.id === id);
          return x ? onSave({ ...x, content }) : null;
        },
      }}
    />
  );
}
